import type { Character } from "@/features/characters/types";
import type { CharactersStorageAdapter } from "@/features/characters/storage/storage-adapter";

const DB_NAME = "cronicas-arton-db";
const DB_VERSION = 1;
const STORE_NAME = "characters";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
  });
}

function withStore<T>(
  mode: IDBTransactionMode,
  callback: (
    store: IDBObjectStore,
    resolve: (value: T) => void,
    reject: (reason?: unknown) => void,
  ) => void,
) {
  return openDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, mode);
        const store = transaction.objectStore(STORE_NAME);

        transaction.onerror = () => reject(transaction.error);
        transaction.oncomplete = () => database.close();

        callback(store, resolve, reject);
      }),
  );
}

export const indexedDbStorage: CharactersStorageAdapter = {
  async ping() {
    if (!("indexedDB" in window)) {
      throw new Error("IndexedDB não disponível");
    }

    const database = await openDatabase();
    database.close();
  },

  async list() {
    return withStore<Character[]>("readonly", (store, resolve, reject) => {
      const request = store.getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve((request.result as Character[]) ?? []);
    });
  },

  async get(id) {
    return withStore<Character | null>("readonly", (store, resolve, reject) => {
      const request = store.get(id);
      request.onerror = () => reject(request.error);
      request.onsuccess = () =>
        resolve((request.result as Character | undefined) ?? null);
    });
  },

  async save(character) {
    return withStore<Character>("readwrite", (store, resolve, reject) => {
      const request = store.put(character);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(character);
    });
  },

  async delete(id) {
    return withStore<void>("readwrite", (store, resolve, reject) => {
      const request = store.delete(id);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  },

  async clear() {
    return withStore<void>("readwrite", (store, resolve, reject) => {
      const request = store.clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  },
};
